import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/supabase';
import type { Session } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import { Loader2, CheckCircle, AlertCircle, Smartphone, Globe, Download } from 'lucide-react';

// Conservative iOS detection — iPhone and iPad (including iPadOS desktop-mode UA).
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
}

// Build the filmple:// deep link preserving the current page's search params and hash.
function buildAppDeepLink(): string {
  return `filmple://auth/callback${window.location.search}${window.location.hash}`;
}

const APP_STORE_URL = 'https://apps.apple.com/app/filmple/id6759590295';

export function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const isOAuthCallback = type !== 'signup' && type !== 'recovery' && type !== 'email_change';
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [continueOnWeb, setContinueOnWeb] = useState(false);
  // Show the iOS interstitial only for callback types the native app can handle
  // AND where the web page won't consume the PKCE code first.
  // - email_change / recovery: web-only completion logic, must not be bypassed.
  // - signup: uses universal links. If this page loaded, the universal link didn't
  //   open the app, so the web client already consumed (or will consume) the PKCE
  //   code. Do NOT try to pass it to filmple:// — let web own the callback.
  const [showInterstitial] = useState(() => {
    if (!isIOS()) return false;
    const type = new URLSearchParams(window.location.search).get('type');
    return type !== 'email_change' && type !== 'recovery' && type !== 'signup';
  });
  // Track iOS signup fallback — show confirmation UI instead of auto-redirecting.
  const [showSignupConfirmed, setShowSignupConfirmed] = useState(false);
  const isIOSSignupFallback = isIOS() && searchParams.get('type') === 'signup';

  // Fire-and-forget: sync CRM profile after auth callback (Journey 5.2 email_confirmed_at)
  const syncIdentify = (token: string) => {
    void apiFetch('/user/identify-sync', { method: 'POST', token }).catch(() => {});
  };

  // Awaited identify-sync with single retry — used for email_change where ordering matters
  const syncIdentifyAwait = async (token: string, userId?: string): Promise<boolean> => {
    const tag = `[identify-sync${userId ? ` user=${userId}` : ''}]`;
    try {
      await apiFetch('/user/identify-sync', { method: 'POST', token });
      return true;
    } catch (err) {
      console.warn(`${tag} first attempt failed, retrying`, err);
      try {
        await apiFetch('/user/identify-sync', { method: 'POST', token });
        return true;
      } catch (retryErr) {
        console.error(`${tag} retry failed — will skip email_changed event`, retryErr);
        return false;
      }
    }
  };

  // On iOS, attempt to open the app immediately (once).
  useEffect(() => {
    if (!showInterstitial || continueOnWeb) return;
    window.location.href = buildAppDeepLink();
  }, [showInterstitial, continueOnWeb]);

  // Run the Supabase callback logic only when:
  // - not on iOS, OR
  // - user clicked "Continue on Web"
  useEffect(() => {
    if (showInterstitial && !continueOnWeb) return;

    const handleCallback = async () => {
      try {
        // Handle password recovery - wait for session then redirect to reset password page
        if (type === 'recovery') {
          setMessage('Verifying password reset link...');

          // Poll for session (Supabase processes hash fragment async)
          const maxAttempts = 8; // ~4 seconds total
          const pollInterval = 500; // 500ms between attempts

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const { data: { session }, error } = await authClient.auth.getSession();

            if (error) {
              console.error('Recovery session error:', error);
              setMessage('Password reset link is invalid or expired. Please request a new one.');
              setStatus('error');
              return;
            }

            if (session) {
              syncIdentify(session.access_token);
              // Session established - safe to redirect
              setMessage('Password reset link verified. Redirecting...');
              setStatus('success');
              setTimeout(() => navigate('/reset-password', { replace: true }), 500);
              return;
            }

            // Wait before next attempt
            if (attempt < maxAttempts - 1) {
              await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
          }

          // No session after all attempts - link likely expired or invalid
          console.error('Recovery: no session after polling');
          setMessage('Password reset link is invalid or expired. Please request a new one.');
          setStatus('error');
          return;
        }

        // Handle email change verification
        if (type === 'email_change') {
          setMessage('Verifying email change...');

          // Poll for session (Supabase processes hash fragment async, same as recovery)
          const maxAttempts = 8;
          const pollInterval = 500;
          let emailChangeSession: Session | null = null;

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const { data: { session }, error } = await authClient.auth.getSession();

            if (error) {
              console.error('Email change session error:', error);
              if (isIOS()) {
                setMessage('Make sure Filmple is installed, then tap this link again directly from your email app.');
              } else {
                setMessage('Email change link is invalid or expired. If you requested this change from the Filmple app, open the link on your iPhone instead.');
              }
              setStatus('error');
              setTimeout(() => navigate('/settings?tab=account', { replace: true }), 5000);
              return;
            }

            if (session) {
              emailChangeSession = session;
              break;
            }

            if (attempt < maxAttempts - 1) {
              await new Promise(resolve => setTimeout(resolve, pollInterval));
            }
          }

          if (!emailChangeSession) {
            console.error('Email change: no session after polling');
            if (isIOS()) {
              setMessage('Make sure Filmple is installed, then tap this link again directly from your email app.');
            } else {
              setMessage('Email change link is invalid or expired. If you requested this change from the Filmple app, open the link on your iPhone instead.');
            }
            setStatus('error');
            setTimeout(() => navigate('/settings?tab=account', { replace: true }), 3000);
            return;
          }

          // Determine if email change is actually complete.
          // Supabase secure double-confirm: first click (old email) does NOT update
          // the email — only the second click (new email) does.
          let pendingEmail: string | null = null;
          try {
            pendingEmail = sessionStorage.getItem('filmple_pending_email');
          } catch {
            // sessionStorage unavailable — use fallback detection below
          }

          const sessionEmail = emailChangeSession.user?.email?.toLowerCase();
          const userId = emailChangeSession.user?.id;

          // Read new_email from getUser() server response, NOT the JWT.
          // Supabase JWTs typically omit new_email — only the API endpoint returns it.
          let pendingNewEmail: string | null | undefined;
          try {
            const { data: { user: freshUser }, error: getUserError } = await authClient.auth.getUser();
            if (getUserError || !freshUser) {
              console.error(
                `[email-change] getUser() returned error/null user, treating as incomplete`,
                { error: getUserError, sessionEmail, userId },
              );
              setMessage('Confirmation received. Please verify from your new email to complete the change.');
              setStatus('success');
              setTimeout(() => navigate('/settings?tab=account', { replace: true }), 1500);
              return;
            }
            pendingNewEmail = (freshUser as { new_email?: string | null }).new_email;
          } catch (getUserErr) {
            // Network/unexpected failure — be conservative: treat as incomplete
            console.error('[email-change] getUser() threw, treating as incomplete', getUserErr);
            setMessage('Confirmation received. Please verify from your new email to complete the change.');
            setStatus('success');
            setTimeout(() => navigate('/settings?tab=account', { replace: true }), 1500);
            return;
          }

          let isComplete: boolean;
          if (pendingEmail) {
            // Same browser: complete when session email matches the requested new email
            isComplete = sessionEmail === pendingEmail.toLowerCase();
          } else if (pendingNewEmail) {
            // Cross-browser, no sessionStorage context: new_email still set = NOT complete
            isComplete = false;
          } else {
            // Cross-browser fallback: no pending marker and no new_email = likely final state
            isComplete = true;
          }

          if (isComplete) {
            // Dedupe: check if we already tracked this completion
            const dedupeKey = `filmple_email_changed_tracked:${userId}:${sessionEmail}`;
            let alreadyTracked = false;
            try {
              alreadyTracked = sessionStorage.getItem(dedupeKey) === '1';
            } catch { /* noop */ }

            if (!alreadyTracked) {
              // Email is updated — sync CRM and fire track event
              const identifyOk = await syncIdentifyAwait(emailChangeSession.access_token, userId);

              if (identifyOk) {
                // Await email_changed POST — only set dedupe key on success
                try {
                  await apiFetch('/user/email-changed', {
                    method: 'POST',
                    token: emailChangeSession.access_token,
                  });
                  try { sessionStorage.setItem(dedupeKey, '1'); } catch { /* noop */ }
                } catch (trackErr) {
                  // POST failed — do NOT set dedupe key so retry is possible on next callback
                  console.error('[email-change] POST /user/email-changed failed', trackErr);
                }
              } else {
                console.warn('[email-change] skipping email_changed event — identify-sync failed');
              }
            }

            // Clean up pending email marker
            try { sessionStorage.removeItem('filmple_pending_email'); } catch { /* noop */ }

            setMessage('Email updated successfully! Redirecting to settings...');
            setStatus('success');
          } else {
            // First confirmation click (old email) — email not changed yet
            setMessage('Confirmation received. Please verify from your new email to complete the change.');
            setStatus('success');
          }

          setTimeout(() => navigate('/settings?tab=account', { replace: true }), 1500);
          return;
        }

        // Handle email confirmation and other auth flows
        // Supabase JS client automatically handles the token exchange
        const { data: { session }, error } = await authClient.auth.getSession();

        if (error) {
          console.error('Auth callback error:', error);
          const msg = error.message?.toLowerCase() ?? '';
          if (msg.includes('banned') || msg.includes('user is banned')) {
            setMessage('Your account has been closed. Please contact support if you need assistance.');
          } else {
            setMessage(error.message || 'Authentication failed');
          }
          setStatus('error');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        // Helper: check if session user is suspended, sign out + show message if so.
        // Returns true if suspended (caller should return), false otherwise.
        const checkSuspendedSession = async (s: Session): Promise<boolean> => {
          try {
            const { createClient } = await import('@supabase/supabase-js');
            const sb = createClient(
              import.meta.env.VITE_SUPABASE_URL,
              import.meta.env.VITE_SUPABASE_ANON_KEY,
              { auth: { persistSession: false }, global: { headers: { Authorization: `Bearer ${s.access_token}` } } }
            );
            const { data: profile } = await sb.from('profiles').select('is_suspended').eq('id', s.user.id).maybeSingle();
            if (profile?.is_suspended) {
              await authClient.auth.signOut();
              setMessage('Your account has been closed. Please contact support if you need assistance.');
              setStatus('error');
              setTimeout(() => navigate('/'), 4000);
              return true;
            }
          } catch {
            // Profile check failed — let AuthContext handle it on the other side
          }
          return false;
        };

        // Helper: handle successful session (after suspended check)
        const handleSessionSuccess = (s: Session) => {
          syncIdentify(s.access_token);
          const type = searchParams.get('type');
          if (type === 'signup') {
            setMessage('Email confirmed!');
            setStatus('success');
            if (isIOSSignupFallback) {
              setShowSignupConfirmed(true);
            } else {
              setTimeout(() => navigate('/'), 1500);
            }
          } else {
            // OAuth or other — silent redirect
            navigate('/', { replace: true });
          }
        };

        if (session) {
          if (await checkSuspendedSession(session)) return;
          handleSessionSuccess(session);
        } else {
          // No session yet, might still be processing
          const hash = window.location.hash;
          if (hash && hash.includes('access_token')) {
            if (!isOAuthCallback) setMessage('Verifying your account...');
            setTimeout(async () => {
              const { data: { session: newSession } } = await authClient.auth.getSession();
              if (newSession) {
                if (await checkSuspendedSession(newSession)) return;
                handleSessionSuccess(newSession);
              } else if (isOAuthCallback) {
                // OAuth callback but no session — likely banned/suspended
                setMessage('Your account has been closed. Please contact support if you need assistance.');
                setStatus('error');
                setTimeout(() => navigate('/'), 4000);
              } else {
                setMessage('Verification complete. Please sign in.');
                setStatus('success');
                if (isIOSSignupFallback) {
                  setShowSignupConfirmed(true);
                } else {
                  setTimeout(() => navigate('/'), 2000);
                }
              }
            }, 1000);
          } else if (isOAuthCallback) {
            // OAuth callback with no hash token — silent redirect
            navigate('/', { replace: true });
          } else {
            setMessage('Verification complete. Please sign in.');
            setStatus('success');
            if (isIOSSignupFallback) {
              setShowSignupConfirmed(true);
            } else {
              setTimeout(() => navigate('/'), 2000);
            }
          }
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setMessage('Something went wrong. Please try again.');
        setStatus('error');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleCallback();
  }, [navigate, searchParams, showInterstitial, continueOnWeb]);

  // iOS interstitial — shown while attempting to open the app
  if (showInterstitial && !continueOnWeb) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
        <div className="text-center p-8 max-w-sm w-full">
          <Loader2 size={48} className="animate-spin text-cyan-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">Opening Filmple...</h1>
          <p className="text-sm text-slate-400 mb-8">
            If you have the app installed, you&apos;ll be taken there to finish signing in.
          </p>
          <div className="flex flex-col gap-3">
            <a
              href={buildAppDeepLink()}
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Smartphone size={18} />
              Open in App
            </a>
            <button
              onClick={() => setContinueOnWeb(true)}
              className="w-full py-3 bg-[#1A1F26] hover:bg-[#252B33] text-slate-300 font-medium rounded-xl transition-colors ring-1 ring-white/[0.08] flex items-center justify-center gap-2"
            >
              <Globe size={18} />
              Continue on Web
            </button>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 text-cyan-400 hover:text-cyan-300 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Get the App
            </a>
          </div>
        </div>
      </div>
    );
  }

  // iOS signup fallback — email confirmed, show safe actions.
  // "Open App" links to app root (NOT the callback URL with the consumed code).
  if (showSignupConfirmed) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-4">
        <div className="text-center p-8 max-w-sm w-full">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">Email Confirmed</h1>
          <p className="text-sm text-slate-400 mb-8">
            Your email has been confirmed. You can continue on web or open the Filmple app.
          </p>
          <div className="flex flex-col gap-3">
            <a
              href="filmple://"
              className="w-full py-3 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Smartphone size={18} />
              Open App
            </a>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full py-3 bg-[#1A1F26] hover:bg-[#252B33] text-slate-300 font-medium rounded-xl transition-colors ring-1 ring-white/[0.08] flex items-center justify-center gap-2"
            >
              <Globe size={18} />
              Continue on Web
            </button>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 text-cyan-400 hover:text-cyan-300 font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Get the App
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (!showInterstitial && isOAuthCallback && status === 'loading') {
    return <div className="min-h-screen bg-[#111111]" aria-hidden="true" />;
  }

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center">
      <div className="text-center p-8">
        {status === 'loading' && (
          <>
            <Loader2 size={48} className="animate-spin text-cyan-400 mx-auto mb-4" />
            {message && <p className="text-lg text-slate-300">{message}</p>}
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
            {message && <p className="text-lg text-green-400">{message}</p>}
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            {message && <p className="text-lg text-red-400">{message}</p>}
            <p className="text-sm text-slate-500 mt-2">Redirecting...</p>
          </>
        )}
      </div>
    </div>
  );
}
